import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";

const baseUrl = API_BASE_URL;

// Platform-realm roles
const PLATFORM_ROLE_OPTIONS = ["platform-admin"];

// Tenant-realm roles
const TENANT_ROLE_OPTIONS = [
  "tenant-admin",
  "enterprise-ai-owner",
  "tevv-lead",
  "risk-reviewer",
  "security-reviewer",
  "head-of-quality",
];

const ROLE_COLOR = {
  "platform-admin":      { bg: "#fce4ec", color: "#b71c1c" },
  "tenant-admin":        { bg: "#e3f2fd", color: "#0d47a1" },
  "enterprise-ai-owner": { bg: "#e8f5e9", color: "#1b5e20" },
  "tevv-lead":           { bg: "#f3e5f5", color: "#6a1b9a" },
  "risk-reviewer":       { bg: "#fce4ec", color: "#c62828" },
  "security-reviewer":   { bg: "#fff8e1", color: "#f57f17" },
  "head-of-quality":     { bg: "#fff3e0", color: "#e65100" },
};

const TeamMembersPage = () => {
  const { user } = useAuth();

  // Determine context: platform-admin uses /admin API, tenant users use /client API
  const isPlatformAdmin = user?.roles?.includes("platform-admin");
  const isTenantAdmin = user?.roles?.includes("tenant-admin");
  const isEnterpriseAiOwner = user?.roles?.includes("enterprise-ai-owner");
  const isTenantContext = isTenantAdmin || isEnterpriseAiOwner;

  const apiPrefix = isPlatformAdmin ? "/admin" : "/client";
  const roleOptions = isPlatformAdmin ? PLATFORM_ROLE_OPTIONS : TENANT_ROLE_OPTIONS;
  const callerRoles = Array.isArray(user?.roles) ? user.roles : [];
  const defaultRole =
    callerRoles.find((r) => roleOptions.includes(r)) ||
    (isPlatformAdmin ? "platform-admin" : "tevv-lead");

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", username: "", firstName: "", lastName: "", tempPassword: "",
    role: defaultRole, projectId: "",
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Role change dialog — V1-1599: supports multiple roles per user
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userToChangeRole, setUserToChangeRole] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [changingRole, setChangingRole] = useState(false);

  // Assign project dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [userToAssign, setUserToAssign] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [userCurrentProjects, setUserCurrentProjects] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Track all user assignments: { username: [{ project_id, project_name }] }
  const [userAssignments, setUserAssignments] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchAllAssignments();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}${apiPrefix}/users`);
      const fetchedUsers = response.data.users || [];
      setUsers(fetchedUsers);
      return fetchedUsers;
    } catch (err) {
      console.error("Error fetching users:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${baseUrl}/projects`);
      if (response.data.success) {
        setProjects(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  const fetchAllAssignments = async () => {
    try {
      const res = await axios.get(`${baseUrl}/all-assignments`);
      if (res.data.success) {
        // Group by username. The API returns only the current tenant's projects.
        const map = {};
        (res.data.data || []).forEach((a) => {
          if (!map[a.username]) map[a.username] = [];
          map[a.username].push(a);
        });
        setUserAssignments(map);
      }
    } catch (err) {
      console.error("Error fetching all assignments:", err);
    }
  };

  const fetchUserAssignments = async (username) => {
    try {
      const res = await axios.get(`${baseUrl}/user-assignments/${username}`);
      if (res.data.success) {
        const data = res.data.data || [];
        setUserAssignments((prev) => ({ ...prev, [username]: data }));
        return data;
      }
    } catch (err) {
      console.error(`Error fetching assignments for ${username}:`, err);
    }
    return [];
  };

  const getRoleChipStyle = (role) => {
    const style = ROLE_COLOR[role] || { bg: "#f5f5f5", color: "#424242" };
    return { bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: "11px" };
  };

  const getInitials = (username) => {
    if (!username) return "U";
    const parts = username.split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return username.substring(0, 2).toUpperCase();
  };

  // ── Invite ────────────────────────────────────────────────────────────────

  const handleInviteOpen = () => {
    setInviteForm({
      email: "", username: "", firstName: "", lastName: "", tempPassword: "",
      role: defaultRole, projectId: "",
    });
    setInviteError("");
    setInviteOpen(true);
  };

  // V1-1599: when invite hits 409 (user exists), append the chosen role to that
  // user instead of failing. Email match is authoritative because Keycloak treats
  // it as unique.
  const findExistingUserByEmailOrUsername = async (email, username) => {
    try {
      const current = users.length ? users : await fetchUsers();
      const lcEmail = (email || "").toLowerCase();
      const lcUser = (username || "").toLowerCase();
      return current.find(
        (u) =>
          (u.email && u.email.toLowerCase() === lcEmail) ||
          (lcUser && u.username && u.username.toLowerCase() === lcUser)
      );
    } catch {
      return null;
    }
  };

  const handleInviteSubmit = async () => {
    const { email, firstName, tempPassword, role } = inviteForm;
    if (!email || !firstName || !tempPassword) {
      setInviteError("Email, first name, and temporary password are required.");
      return;
    }
    try {
      setInviting(true);
      setInviteError("");

      const payload = {
        email: inviteForm.email,
        username: inviteForm.username || undefined,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName || undefined,
        tempPassword: inviteForm.tempPassword,
        role: inviteForm.role,
      };

      try {
        if (isPlatformAdmin) {
          await axios.post(`${baseUrl}/admin/invite-user`, payload);
        } else {
          await axios.post(`${baseUrl}/client/users`, payload);
        }
      } catch (createErr) {
        // V1-1599: user already exists → add the selected role rather than erroring out.
        const status = createErr.response?.status;
        if (status !== 409) throw createErr;

        const existing = await findExistingUserByEmailOrUsername(
          inviteForm.email,
          inviteForm.username
        );
        if (!existing) {
          setInviteError(
            "A user with that email already exists, but we could not look them up to add the role. Refresh and try via Edit role."
          );
          return;
        }
        const currentRoles = Array.isArray(existing.roles) ? existing.roles : [];
        if (currentRoles.includes(inviteForm.role)) {
          setInviteError(
            `User already has the role "${inviteForm.role}". Pick a different role or edit from the user row.`
          );
          return;
        }
        await axios.put(
          `${baseUrl}${apiPrefix}/users/${existing.id}/role`,
          { roles: [...currentRoles, inviteForm.role], mode: "add" }
        );
      }

      // If a project is selected, assign the user to it
      if (inviteForm.projectId && isTenantContext) {
        try {
          const username = (inviteForm.username || inviteForm.email.split("@")[0]).toLowerCase();
          await axios.post(`${baseUrl}/projects/${inviteForm.projectId}/assign`, { username });
        } catch (assignErr) {
          console.warn("Project assignment failed (user still created):", assignErr.message);
        }
      }

      setInviteOpen(false);
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to invite user.";
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (u) => {
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      setDeleting(true);
      await axios.delete(`${baseUrl}${apiPrefix}/users/${userToDelete.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      console.error("Error deleting user:", err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Role change ───────────────────────────────────────────────────────────

  const handleRoleEditClick = (u) => {
    setUserToChangeRole(u);
    // V1-1599: show ALL current realm roles, not just the first one.
    const current = Array.isArray(u.roles) ? u.roles.filter((r) => roleOptions.includes(r)) : [];
    setSelectedRoles(current.length ? current : [defaultRole]);
    setRoleDialogOpen(true);
  };

  const toggleSelectedRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleRoleChangeConfirm = async () => {
    if (!userToChangeRole) return;
    if (!selectedRoles.length) return;
    try {
      setChangingRole(true);
      // Backend accepts `roles: [...]` for multi-role reconcile.
      const resp = await axios.put(
        `${baseUrl}${apiPrefix}/users/${userToChangeRole.id}/role`,
        { roles: selectedRoles }
      );
      const newRoles = Array.isArray(resp.data?.roles) ? resp.data.roles : selectedRoles;
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userToChangeRole.id ? { ...u, roles: newRoles } : u
        )
      );
      setRoleDialogOpen(false);
      setUserToChangeRole(null);
    } catch (err) {
      console.error("Error changing role:", err);
    } finally {
      setChangingRole(false);
    }
  };

  // ── Assign project ─────────────────────────────────────────────────────────

  const handleAssignClick = async (u) => {
    setUserToAssign(u);
    setSelectedProjectIds([]);
    setAssigning(false);
    setAssignDialogOpen(true);
    const currentAssignments = await fetchUserAssignments(u.username);
    setUserCurrentProjects((currentAssignments || []).map((a) => a.project_id));
  };

  const handleToggleProject = (projectId) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const handleAssignSelected = async () => {
    if (!userToAssign || selectedProjectIds.length === 0) return;
    try {
      setAssigning(true);
      await Promise.all(
        selectedProjectIds.map((pid) =>
          axios.post(`${baseUrl}/projects/${pid}/assign`, {
            username: userToAssign.username,
            email: userToAssign.email,
          })
        )
      );
      await fetchUserAssignments(userToAssign.username);
      await fetchAllAssignments();
      setSelectedProjectIds([]);
      setAssignDialogOpen(false);
      setUserToAssign(null);
    } catch (err) {
      console.error("Error assigning projects:", err);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignSelected = async () => {
    if (!userToAssign || selectedProjectIds.length === 0) return;
    try {
      setAssigning(true);
      await Promise.all(
        selectedProjectIds.map((pid) =>
          axios.delete(`${baseUrl}/projects/${pid}/assign/${userToAssign.username}`)
        )
      );
      await fetchUserAssignments(userToAssign.username);
      await fetchAllAssignments();
      setSelectedProjectIds([]);
      setAssignDialogOpen(false);
      setUserToAssign(null);
    } catch (err) {
      console.error("Error unassigning projects:", err);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignProject = async (username, projectId) => {
    try {
      await axios.delete(`${baseUrl}/projects/${projectId}/assign/${username}`);
      await fetchUserAssignments(username);
      await fetchAllAssignments();
    } catch (err) {
      console.error("Error unassigning project:", err);
    }
  };

  // ── Permissions ───────────────────────────────────────────────────────────

  const canEditRoles = isPlatformAdmin || isTenantAdmin;
  const canDeleteUsers = isPlatformAdmin || isTenantAdmin;
  const canAssignProjects = isEnterpriseAiOwner;
  const canInvite = isPlatformAdmin || isTenantAdmin;

  // ── Filter + stats ────────────────────────────────────────────────────────

  const filteredUsers = users.filter(
    (u) =>
      (u.username || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.roles || []).some((r) => r.toLowerCase().includes(search.toLowerCase()))
  );

  const statCards = [
    { title: "Total Members", value: users.length },
    ...(isTenantContext
      ? [
          { title: "AI Owners", value: users.filter((u) => u.roles?.includes("enterprise-ai-owner")).length },
          { title: "TEVV Lead", value: users.filter((u) => u.roles?.includes("tevv-lead")).length },
          { title: "Reviewers", value: users.filter((u) => u.roles?.some(r => ["risk-reviewer", "security-reviewer", "head-of-quality"].includes(r))).length },
        ]
      : [
          { title: "Platform Admins", value: users.filter((u) => u.roles?.includes("platform-admin")).length },
        ]
    ),
  ];

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* HEADER */}
      <Typography variant="h5" fontWeight={700}>
        Team Members
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {isTenantContext ? "Manage your organisation's team members and assign them to projects" : "Manage platform administrators"}
      </Typography>

      {/* ACTIONS ROW */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
        <TextField
          placeholder="Search Members"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {canInvite && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleInviteOpen}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 2.5,
              fontWeight: 600,
              color: "#0094FB",
              borderColor: "#0094FB",
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#E8F3FF", borderColor: "#0094FB" },
            }}
          >
            Invite User
          </Button>
        )}
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        {statCards.map((stat, i) => (
          <Grid key={i} item xs={6} md={3}>
            <Card
              elevation={0}
              sx={{
                height: 72,
                borderRadius: "12px",
                border: "1px solid #D9D9D9",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography fontSize="14px" color="#344054">{stat.title}</Typography>
              <Typography fontSize="22px" fontWeight={400} mt={0.5}>{stat.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* USER CARDS */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Typography color="text.secondary">
            {search ? "No members found matching your search" : "No members found."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredUsers.map((u) => (
            <Grid item xs={12} md={6} key={u.id}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
                  p: 2.5,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,0,0,0.08)" },
                }}
              >
                <Box display="flex" alignItems="start" gap={2}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: "#0094FB", fontSize: "18px", fontWeight: 600 }}>
                    {getInitials(u.username)}
                  </Avatar>

                  <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Box>
                        <Typography fontWeight={600} fontSize="16px">
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                        </Typography>
                        {u.firstName && (
                          <Typography fontSize="12px" color="text.secondary">@{u.username}</Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <EmailIcon sx={{ fontSize: 14, color: "#757575" }} />
                          <Typography fontSize="13px" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Box>

                      {/* Role + actions */}
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                        <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end">
                          {Array.isArray(u.roles) && u.roles.length > 0
                            ? u.roles.map((role, index) => (
                                <Chip
                                  key={`${u.id}-${role}-${index}`}
                                  label={role}
                                  size="small"
                                  sx={getRoleChipStyle(role)}
                                />
                              ))
                            : (
                                <Chip
                                  label="no role"
                                  size="small"
                                  sx={getRoleChipStyle()}
                                />
                              )}
                        </Box>
                        {canAssignProjects && (
                          <Tooltip title="Assign to project">
                            <IconButton size="small" onClick={() => handleAssignClick(u)} sx={{ color: "#6C47FF" }}>
                              <AssignmentIndIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEditRoles && (
                          <Tooltip title="Change role">
                            <IconButton size="small" onClick={() => handleRoleEditClick(u)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteUsers && (
                          <Tooltip title="Delete user">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(u)}
                              sx={{ color: "#d32f2f", "&:hover": { backgroundColor: "rgba(211,47,47,0.08)" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5} mt={1} pt={1} borderTop="1px solid #f0f0f0" flexWrap="wrap">
                      <PersonIcon sx={{ fontSize: 14, color: "#757575" }} />
                      <Typography fontSize="12px" color="text.secondary" mr={1}>
                        {u.enabled ? "Active" : "Disabled"}
                      </Typography>

                      {/* Assigned projects — current tenant only */}
                      {(userAssignments[u.username] || []).length > 0 && (
                        <>
                          <Box sx={{ width: "1px", height: 14, bgcolor: "#e0e0e0", mx: 0.5 }} />
                          <FolderOutlinedIcon sx={{ fontSize: 14, color: "#6C47FF" }} />
                          {(userAssignments[u.username] || []).map((a) => (
                            <Chip
                              key={a.project_id}
                              label={a.project_name}
                              size="small"
                              sx={{
                                fontSize: "11px", fontWeight: 500, height: 22,
                                bgcolor: "#F3F0FF", color: "#6C47FF", border: "1px solid #E0D7FF",
                              }}
                            />
                          ))}
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── INVITE DIALOG ─────────────────────────────────────────────────── */}
      <Dialog open={inviteOpen} onClose={() => !inviting && setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Email *"
            fullWidth
            size="small"
            value={inviteForm.email}
            onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Box display="flex" gap={2}>
            <TextField
              label="First Name *"
              fullWidth
              size="small"
              value={inviteForm.firstName}
              onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))}
            />
            <TextField
              label="Last Name"
              fullWidth
              size="small"
              value={inviteForm.lastName}
              onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))}
            />
          </Box>
          <TextField
            label="Username (optional — defaults to email prefix)"
            fullWidth
            size="small"
            value={inviteForm.username}
            onChange={(e) => setInviteForm((f) => ({ ...f, username: e.target.value }))}
          />
          <TextField
            label="Temporary Password *"
            fullWidth
            size="small"
            type="password"
            value={inviteForm.tempPassword}
            onChange={(e) => setInviteForm((f) => ({ ...f, tempPassword: e.target.value }))}
            helperText="User will be required to change this on first login."
          />
          <FormControl fullWidth size="small">
            <InputLabel>Role *</InputLabel>
            <Select
              value={inviteForm.role}
              label="Role *"
              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
            >
              {roleOptions.map((r) => (
                <MenuItem key={r} value={r} sx={{ textTransform: "capitalize" }}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Project assignment — shown in tenant context when projects exist */}
          {isTenantContext && projects.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Assign to Project (optional)</InputLabel>
              <Select
                value={inviteForm.projectId}
                label="Assign to Project (optional)"
                onChange={(e) => setInviteForm((f) => ({ ...f, projectId: e.target.value }))}
              >
                <MenuItem value="">
                  <em>None — assign later</em>
                </MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.project_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {inviteError && (
            <Typography color="error" fontSize="13px">{inviteError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained" disabled={inviting}>
            {inviting ? <CircularProgress size={20} /> : "Send Invite"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE DIALOG ─────────────────────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{userToDelete?.username}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setUserToDelete(null); }} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ROLE CHANGE DIALOG (multi-role, V1-1599) ──────────────────────── */}
      <Dialog open={roleDialogOpen} onClose={() => !changingRole && setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Roles</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Typography mb={1} fontSize="14px">
            Select one or more roles for <strong>{userToChangeRole?.username}</strong>.
          </Typography>
          <Typography mb={2} fontSize="12px" color="text.secondary">
            Current roles:&nbsp;
            {(userToChangeRole?.roles || []).length
              ? (userToChangeRole.roles || []).map((r) => (
                  <Chip
                    key={r}
                    label={r}
                    size="small"
                    sx={{ ...getRoleChipStyle(r), mr: 0.5, mb: 0.5 }}
                  />
                ))
              : <em>none</em>}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {roleOptions.map((r) => {
              const checked = selectedRoles.includes(r);
              return (
                <Chip
                  key={r}
                  label={r}
                  onClick={() => toggleSelectedRole(r)}
                  variant={checked ? "filled" : "outlined"}
                  sx={
                    checked
                      ? { ...getRoleChipStyle(r), cursor: "pointer", fontWeight: 700 }
                      : { cursor: "pointer", borderColor: "#ccc", color: "#666" }
                  }
                />
              );
            })}
          </Box>
          {!selectedRoles.length && (
            <Typography mt={1.5} fontSize="12px" color="error">
              Pick at least one role.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRoleDialogOpen(false); setUserToChangeRole(null); }} disabled={changingRole}>
            Cancel
          </Button>
          <Button
            onClick={handleRoleChangeConfirm}
            variant="contained"
            disabled={changingRole || !selectedRoles.length}
          >
            {changingRole ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ASSIGN PROJECT DIALOG (Multi-select checkboxes) ─────────────── */}
      <Dialog open={assignDialogOpen} onClose={() => !assigning && setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderOutlinedIcon sx={{ color: "#6C47FF" }} />
            Manage Project Assignments
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Typography mb={2} fontSize="14px">
            Select projects for <strong>{userToAssign?.firstName ? `${userToAssign.firstName} ${userToAssign.lastName || ""}`.trim() : userToAssign?.username}</strong>.
            Check to assign, uncheck to unassign.
          </Typography>

          {projects.length === 0 ? (
            <Typography fontSize="13px" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No projects available. Create a project first by onboarding an application.
            </Typography>
          ) : (
            <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden" }}>
              {projects.map((p, i) => {
                const isChecked = selectedProjectIds.includes(p.id);
                const isAssigned = userCurrentProjects.includes(p.id);

                return (
                  <Box
                    key={p.id}
                    onClick={() => handleToggleProject(p.id)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 2, py: 1.5, cursor: "pointer",
                      bgcolor: isChecked ? "#F3F0FF" : "#fff",
                      borderBottom: i < projects.length - 1 ? "1px solid #F3F4F6" : "none",
                      "&:hover": { bgcolor: isChecked ? "#EDE9FF" : "#F9FAFB" },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center",
                        border: isChecked ? "none" : "2px solid #D1D5DB",
                        bgcolor: isChecked ? "#6C47FF" : "transparent",
                      }}>
                        {isChecked && (
                          <Typography sx={{ color: "#fff", fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>&#10003;</Typography>
                        )}
                      </Box>
                      <Typography fontSize="14px" fontWeight={500} color="#1E1E1E">{p.project_name}</Typography>
                    </Box>

                    {isAssigned && (
                      <Chip label="Assigned" size="small" sx={{ fontSize: "10px", height: 20, bgcolor: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0" }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {selectedProjectIds.length > 0 && (
            <Typography fontSize="12px" color="#707070" mt={1.5}>
              {selectedProjectIds.length} project{selectedProjectIds.length > 1 ? "s" : ""} selected: {selectedProjectIds.map((id) => projects.find((p) => p.id === id)?.project_name).join(", ")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setAssignDialogOpen(false); setUserToAssign(null); setSelectedProjectIds([]); }}
            disabled={assigning}
            sx={{ textTransform: "none", fontWeight: 600, color: "#707070" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnassignSelected}
            variant="outlined"
            disabled={assigning || selectedProjectIds.length === 0}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "6px", px: 3,
              borderColor: selectedProjectIds.length > 0 ? "#DC2626" : "#D9D9D9",
              color: selectedProjectIds.length > 0 ? "#DC2626" : "#D9D9D9",
              "&:hover": selectedProjectIds.length > 0 ? { borderColor: "#B91C1C", bgcolor: "#FEF2F2" } : {},
            }}
          >
            {assigning ? <CircularProgress size={18} /> : "Unassign"}
          </Button>
          <Button
            onClick={handleAssignSelected}
            variant="contained"
            disabled={assigning || selectedProjectIds.length === 0}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "6px", px: 3,
              bgcolor: selectedProjectIds.length > 0 ? "#6C47FF" : "#E5E7EB",
              color: selectedProjectIds.length > 0 ? "#fff" : "#9CA3AF",
              "&:hover": selectedProjectIds.length > 0 ? { bgcolor: "#5A3AD9" } : {},
            }}
          >
            {assigning ? <CircularProgress size={18} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamMembersPage;
